// src/components/modals/ModalImportarExcel.jsx
// Modal genérico para importar registros desde Excel asociados a un cliente.
// El endpoint debe aceptar multipart { file, clienteId } y responder
// { resumen: { totalFilas, creados, duplicados, errores }, detalles: [{ fila, status, correccion }] }.
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import {
  X,
  FileSpreadsheet,
  UploadCloud,
  Download,
  LoaderPinwheel,
  CheckCircle2,
  AlertTriangle,
  XCircle,
} from "lucide-react";
import SelectField from "../ui/SelectField";
import { descargarArchivo } from "../../utils/descargas";

const ResumenItem = ({ icon: Icon, label, value, className }) => (
  <div className={`flex items-center gap-2 rounded-lg border px-3 py-2 ${className}`}>
    <Icon className="h-4 w-4 shrink-0" />
    <span className="text-xs font-medium">{label}</span>
    <span className="ml-auto text-sm font-bold">{value}</span>
  </div>
);

const ModalImportarExcel = ({
  titulo = "Importar desde Excel",
  descripcion,
  endpoint,
  plantilla, // { path, filename }
  onClose,
  onImported,
}) => {
  const [clientes, setClientes] = useState([]);
  const [cliente, setCliente] = useState("");
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resultado, setResultado] = useState(null);
  const inputRef = useRef(null);

  const API = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("token");

  useEffect(() => {
    axios
      .get(`${API}/clientes`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setClientes(res.data || []))
      .catch(() => toast.error("Error al cargar clientes"));
  }, [API, token]);

  const elegirArchivo = (f) => {
    if (!f) return;
    if (!/\.xlsx?$/i.test(f.name)) {
      toast.warning("Selecciona un archivo .xlsx o .xls");
      return;
    }
    setFile(f);
    setResultado(null);
  };

  const handleImport = async () => {
    if (!cliente) return toast.warning("Selecciona un cliente");
    if (!file) return toast.warning("Selecciona un archivo Excel");

    const formData = new FormData();
    formData.append("file", file);
    formData.append("clienteId", cliente);

    setLoading(true);
    try {
      const res = await axios.post(`${API}${endpoint}`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setResultado(res.data);
      setFile(null);
      const creados = res.data?.resumen?.creados || 0;
      toast.success(`Importación finalizada: ${creados} registro${creados !== 1 ? "s" : ""} creado${creados !== 1 ? "s" : ""}`);
      onImported?.(res.data);
    } catch (err) {
      toast.error(err.response?.data?.message || "Error al importar el archivo");
    } finally {
      setLoading(false);
    }
  };

  const handlePlantilla = async () => {
    try {
      await descargarArchivo(plantilla.path, plantilla.filename);
    } catch {
      toast.error("No se pudo descargar el formato");
    }
  };

  const resumen = resultado?.resumen;
  // Errores y duplicados primero: son los que requieren acción
  const detalles = [...(resultado?.detalles || [])].sort(
    (a, b) => Number(a.status.includes("✅")) - Number(b.status.includes("✅")),
  );

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-6 pt-5 pb-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-600 rounded-xl">
              <FileSpreadsheet className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">{titulo}</h2>
              {descripcion && <p className="text-xs text-gray-500">{descripcion}</p>}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            aria-label="Cerrar"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4 overflow-y-auto">
          <SelectField
            label="Cliente *"
            name="cliente"
            value={cliente}
            options={clientes}
            placeholder="Selecciona el cliente"
            onChange={(e) => setCliente(e.target.value)}
          />

          <div
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              elegirArchivo(e.dataTransfer.files?.[0]);
            }}
            className={`cursor-pointer rounded-xl border-2 border-dashed px-4 py-8 text-center transition-colors ${
              dragOver
                ? "border-blue-400 bg-blue-50"
                : file
                  ? "border-green-300 bg-green-50/50"
                  : "border-gray-200 hover:border-blue-300 hover:bg-gray-50"
            }`}
          >
            <UploadCloud className={`h-8 w-8 mx-auto mb-2 ${file ? "text-green-500" : "text-gray-300"}`} />
            {file ? (
              <p className="text-sm font-medium text-gray-700 break-all">{file.name}</p>
            ) : (
              <>
                <p className="text-sm font-medium text-gray-700">
                  Arrastra tu archivo aquí o haz clic para seleccionarlo
                </p>
                <p className="text-xs text-gray-400 mt-1">Formatos: .xlsx, .xls · Máx. 5 MB</p>
              </>
            )}
            <input
              ref={inputRef}
              type="file"
              accept=".xlsx,.xls"
              className="hidden"
              onChange={(e) => {
                elegirArchivo(e.target.files?.[0]);
                e.target.value = "";
              }}
            />
          </div>

          {resumen && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <ResumenItem icon={FileSpreadsheet} label="Filas" value={resumen.totalFilas} className="border-gray-200 text-gray-600" />
                <ResumenItem icon={CheckCircle2} label="Creados" value={resumen.creados} className="border-green-200 bg-green-50 text-green-700" />
                <ResumenItem icon={AlertTriangle} label="Duplicados" value={resumen.duplicados} className="border-amber-200 bg-amber-50 text-amber-700" />
                <ResumenItem icon={XCircle} label="Errores" value={resumen.errores} className="border-red-200 bg-red-50 text-red-700" />
              </div>

              {detalles.length > 0 && (
                <div className="overflow-auto border border-gray-200 rounded-lg max-h-64">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-100 text-gray-600 sticky top-0">
                      <tr>
                        <th className="text-left px-3 py-2">Fila</th>
                        <th className="text-left px-3 py-2">Resultado</th>
                        <th className="text-left px-3 py-2">Corrección sugerida</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {detalles.map((d, i) => (
                        <tr key={`${d.fila}-${i}`}>
                          <td className="px-3 py-1.5 font-mono">{d.fila}</td>
                          <td className="px-3 py-1.5 whitespace-nowrap">{d.status}</td>
                          <td className="px-3 py-1.5 text-gray-500">{d.correccion || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-2 px-6 py-4 border-t border-gray-100">
          {plantilla ? (
            <button
              type="button"
              onClick={handlePlantilla}
              className="flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-green-700 border border-green-200 bg-green-50 rounded-lg hover:bg-green-100 transition-colors"
            >
              <Download className="h-4 w-4" /> Descargar formato
            </button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 sm:flex-none px-4 py-2 text-sm font-medium border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              {resultado ? "Cerrar" : "Cancelar"}
            </button>
            <button
              type="button"
              onClick={handleImport}
              disabled={loading}
              className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50"
            >
              {loading ? (
                <>
                  <LoaderPinwheel className="h-4 w-4 animate-spin" /> Importando…
                </>
              ) : (
                <>
                  <UploadCloud className="h-4 w-4" /> Importar
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModalImportarExcel;
